// Procedural drawing — no image assets. Each `shape` in a sprite
// component maps to a draw routine here. Content references shapes by
// name; this file is the only place that knows how a
// "stick"/"cat"/"blast" actually looks.

import { FIGURE_DEFAULT } from "../../../content/figure.js";
import { WEAPONS } from "../../../content/index.js";
import { ITEMS } from "../../../content/pickups.js";
import { WEAPON_ART, artBack, gustaFace, drawWeaponIcon } from "./weapons-art.js";
import { paintColor } from "../../../engine/palette.js";

function face(g, kind, hx, hy, hr, color) {
    g.save();
    g.lineWidth = Math.max(1.5, hr * 0.18);
    g.strokeStyle = "#0d1017";
    g.fillStyle = "#0d1017";
    const eye = (dx, r = hr * 0.16) => {
        g.beginPath();
        g.arc(hx + dx, hy - hr * 0.1, r, 0, Math.PI * 2);
        g.fill();
    };
    if (kind === "happy") {
        eye(-hr * 0.35); eye(hr * 0.35);
        g.beginPath(); g.arc(hx, hy + hr * 0.1, hr * 0.4, 0.15 * Math.PI, 0.85 * Math.PI); g.stroke();
    } else if (kind === "troll") {
        eye(-hr * 0.35, hr * 0.12); eye(hr * 0.35, hr * 0.12);
        g.beginPath(); g.moveTo(hx - hr * 0.6, hy + hr * 0.25);
        g.quadraticCurveTo(hx, hy + hr * 0.75, hx + hr * 0.6, hy + hr * 0.25); g.stroke();
    } else if (kind === "derp") {
        g.fillStyle = "#fff";
        g.beginPath(); g.arc(hx - hr * 0.3, hy, hr * 0.3, 0, 7); g.fill();
        g.beginPath(); g.arc(hx + hr * 0.35, hy - hr * 0.1, hr * 0.22, 0, 7); g.fill();
        g.fillStyle = "#0d1017";
        eye(-hr * 0.3, hr * 0.1); eye(hr * 0.38, hr * 0.08);
    } else if (kind === "over9000") {
        g.beginPath(); g.moveTo(hx - hr * 0.6, hy - hr * 0.4); g.lineTo(hx - hr * 0.1, hy - hr * 0.1);
        g.moveTo(hx + hr * 0.6, hy - hr * 0.4); g.lineTo(hx + hr * 0.1, hy - hr * 0.1); g.stroke();
        eye(-hr * 0.35, hr * 0.12); eye(hr * 0.35, hr * 0.12);
        g.beginPath(); g.arc(hx, hy + hr * 0.35, hr * 0.35, 0, Math.PI * 2); g.fill();
    }
    g.restore();
}

function stick(g, e) {
    const r = e.r * (e.sc || 1);

    // Proportions and pose come from the figure (data/figure.json via
    // content/figure.js) — the single source of truth, so motion.js
    // derives the feet's ground contact from the same numbers.
    // Reshape the figure THERE.
    //
    // `e.fig` lets a caller supply a DIFFERENT figure: the stick
    // figure tool passes one built from unsaved, in-progress values
    // so it previews edits through this exact renderer rather than a
    // lookalike copy of it. The game never sets it.
    const F = e.fig || FIGURE_DEFAULT;
    const P = F.STICK;

    const LINE_W    = Math.max(2, r * P.LINE_W);
    const HEAD_R    = r * P.HEAD_R;
    const HEAD_Y    = r * P.HEAD_Y;
    const NECK_Y    = HEAD_Y + HEAD_R;         // where the body line starts (chin)
    const HIP_Y     = r * P.HIP_Y;
    const HIP_DX    = r * P.HIP_DX;
    const THIGH_LEN = r * P.THIGH_LEN;
    const SHIN_LEN  = r * P.SHIN_LEN;
    const KNEE_R    = Math.max(1.6, r * P.KNEE_R);
    const SHOULDER_Y = r * P.SHOULDER_Y;
    const UPPER_ARM_LEN = r * P.UPPER_ARM_LEN;
    const FOREARM_LEN   = r * P.FOREARM_LEN;
    const ELBOW_R   = Math.max(1.4, r * P.ELBOW_R);

    g.save();
    g.translate(e.x, e.y);
    g.strokeStyle = e.c;
    g.lineWidth = LINE_W;
    g.lineCap = "round";
    // body
    g.beginPath();
    g.moveTo(0, NECK_Y);
    g.lineTo(0, HIP_Y);
    g.stroke();
    // legs — two segments each (thigh + shin). figure.js solves the
    // pose (walking or standing); here we just do the forward
    // kinematics from its absolute segment angles (measured from
    // straight-down, + = facing direction).
    const sgn = e.fl ? -1 : 1;           // swing the legs about the WALKING direction (sprite.flip), not the aim
    const pose = F.poseAt(e.gaitPhase || 0, e.walk, e.gait, sgn);
    for (const side of ["left", "right"]) {
        const hipX = (side === "left" ? -1 : 1) * HIP_DX;
        const a1 = pose.legs[side].thigh, a2 = pose.legs[side].shin;
        const kx = hipX + sgn * Math.sin(a1) * THIGH_LEN;
        const ky = HIP_Y + Math.cos(a1) * THIGH_LEN;
        const fx = kx + sgn * Math.sin(a2) * SHIN_LEN;
        const fy = ky + Math.cos(a2) * SHIN_LEN;
        g.beginPath();
        g.moveTo(hipX, HIP_Y); g.lineTo(kx, ky); g.lineTo(fx, fy);
        g.stroke();
        // knee joint marker so the two-segment leg is obvious even at
        // rest
        g.beginPath();
        g.fillStyle = e.c;
        g.arc(kx, ky, KNEE_R, 0, Math.PI * 2);
        g.fill();
    }
    // arms — two segments each (upper arm + forearm), same forward
    // kinematics as the legs. The right arm holds the weapon, so it's
    // solved to aim at e.a. The left is whatever the weapon leaves
    // it: a one-handed weapon leaves it free, so it takes the pose
    // (splayed at rest, counter-swinging when moving); a two-handed
    // one claims it for the grip.
    //
    // Everything here is in BODY RADII (the figure's own units, what
    // figure.js speaks) and only multiplied by r at the moment it's
    // drawn. Mixing the two is how you get a hand that's right at one
    // size and wrong at every other.
    // `wp` is whatever is IN YOUR HANDS (Set 14) — any item, not only a gun. A weapon still drives the
    // two-handed grip below; a non-weapon is simply held.
    const weapon = WEAPONS[e.wp] || null;
    const art = weapon && WEAPON_ART[weapon.shape];
    const held = e.wp || "";
    const aim = F.aimArm(e.a, sgn);
    const hand = F.handAt(aim, sgn);          // screen-space x, in radii, from the origin

    let leftArm = pose.arms.left;
    if (art && weapon.grip === "two") {
        // The off hand grips the weapon BEHIND the main hand — where
        // a rear hand actually goes on a rifle, and the only place it
        // CAN go: both arms hang off the same shoulder point, so a
        // grip further out than the main hand is further than the arm
        // reaching for it is long, and the hand would float short of
        // it.
        const back = weapon.grab ?? artBack(weapon.shape);
        const gx = hand.x - Math.cos(e.a) * back;
        const gy = hand.y - Math.sin(e.a) * back;
        // armTo speaks the facing frame, relative to the shoulder;
        // the renderer mirrors x back out. hand.x already had sgn
        // applied, so undo it on the way in.
        leftArm = F.armTo(sgn * gx, gy - P.SHOULDER_Y);
    }
    const arms = { left: leftArm, right: aim };
    for (const arm of [arms.left, arms.right]) {
        const ex = sgn * Math.sin(arm.upper) * UPPER_ARM_LEN;
        const ey = SHOULDER_Y + Math.cos(arm.upper) * UPPER_ARM_LEN;
        const hx = ex + sgn * Math.sin(arm.fore) * FOREARM_LEN;
        const hy = ey + Math.cos(arm.fore) * FOREARM_LEN;
        g.beginPath();
        g.moveTo(0, SHOULDER_Y); g.lineTo(ex, ey); g.lineTo(hx, hy);
        g.stroke();
        // elbow joint marker, so the two segments read as an arm and
        // not a bent stick
        g.beginPath();
        g.fillStyle = e.c;
        g.arc(ex, ey, ELBOW_R, 0, Math.PI * 2);
        g.fill();
    }

    // the weapon, in the main hand and pointing where you're aiming.
    // Drawn after the arms so it sits in front of the hand rather
    // than behind it, and in its own frame — hand at the origin, +x
    // down the aim — so the art never has to think about where the
    // player is or which way they face.
    if (held) {
        g.save();
        g.translate(hand.x * r, hand.y * r);
        g.rotate(e.a);
        // Aiming left would draw the weapon upside-down, since
        // rotating past vertical flips it. Mirror it back so it
        // always reads the right way up, like a real side-on sprite
        // would.
        if (Math.cos(e.a) < 0) g.scale(1, -1);
        g.__aim = Math.cos(e.a) < 0 ? -e.a : e.a; // so an icon fallback can stand upright in this frame
        drawHeldItem(g, held, r, e.c);
        g.__aim = 0;
        g.restore();
    }

    // head
    g.beginPath();
    g.arc(0, HEAD_Y, HEAD_R, 0, Math.PI * 2);
    g.fillStyle = e.c;
    g.globalAlpha = 0.25;
    g.fill();
    g.globalAlpha = 1;
    g.stroke();
    face(g, e.f, 0, HEAD_Y, HEAD_R, e.c);
    g.restore();
}

function cat(g, e) {
    const r = e.r * (e.sc || 1) * 1.1;
    g.save();
    g.translate(e.x, e.y);
    g.fillStyle = "#111318";
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill();
    // ears
    g.beginPath();
    g.moveTo(-r * 0.7, -r * 0.5); g.lineTo(-r * 0.3, -r * 1.1); g.lineTo(-r * 0.05, -r * 0.5);
    g.moveTo(r * 0.7, -r * 0.5); g.lineTo(r * 0.3, -r * 1.1); g.lineTo(r * 0.05, -r * 0.5);
    g.fill();
    // glowing eyes
    g.fillStyle = "#ffe24a";
    g.beginPath(); g.ellipse(-r * 0.35, -r * 0.05, r * 0.2, r * 0.3, 0, 0, 7); g.fill();
    g.beginPath(); g.ellipse(r * 0.35, -r * 0.05, r * 0.2, r * 0.3, 0, 0, 7); g.fill();
    g.fillStyle = "#111318";
    g.fillRect(-r * 0.4, -r * 0.35, r * 0.1, r * 0.6);
    g.fillRect(r * 0.3, -r * 0.35, r * 0.1, r * 0.6);
    g.restore();
}

function orb(g, e) {
    const r = e.r * (e.sc || 1);
    if (e.tr === "rainbow") {
        const cols = ["#ff004d", "#ff8800", "#ffe24a", "#33dd55", "#33aaff", "#aa55ff"];
        for (let i = 0; i < cols.length; i++) {
            g.globalAlpha = 0.5 - i * 0.06;
            g.fillStyle = cols[i];
            const bx = e.x - Math.cos(e.a) * i * r * 1.3;
            const by = e.y - Math.sin(e.a) * i * r * 1.3;
            g.beginPath(); g.arc(bx, by, r * 0.9, 0, Math.PI * 2); g.fill();
        }
        g.globalAlpha = 1;
    }
    g.fillStyle = e.c;
    g.beginPath(); g.arc(e.x, e.y, r, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#fff";
    g.globalAlpha = 0.6;
    g.beginPath(); g.arc(e.x - r * 0.3, e.y - r * 0.3, r * 0.35, 0, Math.PI * 2); g.fill();
    g.globalAlpha = 1;
}

function blast(g, e) {
    const r = e.r * (e.sc || 1);
    g.save();
    g.translate(e.x, e.y);
    g.rotate(e.a);
    const grd = g.createLinearGradient(-r * 2, 0, r * 2.4, 0);
    grd.addColorStop(0, "rgba(255,226,74,0)");
    grd.addColorStop(0.6, "#ffe24a");
    grd.addColorStop(1, "#fff");
    g.fillStyle = grd;
    g.beginPath(); g.ellipse(0, 0, r * 2.2, r * 0.9, 0, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#fff";
    g.beginPath(); g.arc(r * 1.3, 0, r * 0.7, 0, Math.PI * 2); g.fill();
    g.restore();
}

// Shoop Da Whoop's "OH SHI—" laser: a long tapered beam with a
// blazing core and a bright round head at the business end.
function beam(g, e) {
    const r = e.r * (e.sc || 1);
    g.save();
    g.translate(e.x, e.y);
    g.rotate(e.a);
    // outer glow
    const glow = g.createLinearGradient(-r * 3.4, 0, r * 2.2, 0);
    glow.addColorStop(0, "rgba(90,210,255,0)");
    glow.addColorStop(0.55, "rgba(120,225,255,.65)");
    glow.addColorStop(1, "rgba(255,255,255,.9)");
    g.fillStyle = glow;
    g.beginPath(); g.ellipse(-r * 0.5, 0, r * 3.0, r * 1.05, 0, 0, Math.PI * 2); g.fill();
    // hot yellow-white core
    const core = g.createLinearGradient(-r * 2.6, 0, r * 2.0, 0);
    core.addColorStop(0, "rgba(255,226,74,0)");
    core.addColorStop(0.7, "#ffe24a");
    core.addColorStop(1, "#ffffff");
    g.fillStyle = core;
    g.beginPath(); g.ellipse(-r * 0.4, 0, r * 2.3, r * 0.42, 0, 0, Math.PI * 2); g.fill();
    // impact head
    g.fillStyle = "#fff";
    g.beginPath(); g.arc(r * 1.5, 0, r * 0.8, 0, Math.PI * 2); g.fill();
    g.restore();
}

// The legally-distinct "Poptart Creature": a frosted-pastry body with
// sprinkles, a little cat head poking out the front, and a rainbow
// contrail. Evokes a certain flying cat without being it.
function poptart(g, e) {
    const r = e.r * (e.sc || 1);
    // rainbow contrail (streaks trailing opposite the travel
    // direction)
    if (e.tr === "rainbow") {
        const cols = ["#ff004d", "#ff8800", "#ffe24a", "#33dd55", "#33aaff", "#aa55ff"];
        const bw = r * 0.42;
        for (let i = 0; i < cols.length; i++) {
            g.globalAlpha = 0.85;
            g.strokeStyle = cols[i];
            g.lineWidth = bw;
            const off = (i - (cols.length - 1) / 2) * bw;
            const px = -Math.cos(e.a), py = -Math.sin(e.a);           // backward
            const nx = -Math.sin(e.a), ny = Math.cos(e.a);            // perpendicular
            g.beginPath();
            g.moveTo(e.x + nx * off, e.y + ny * off);
            g.lineTo(e.x + nx * off + px * r * 3.4, e.y + ny * off + py * r * 3.4);
            g.stroke();
        }
        g.globalAlpha = 1;
    }
    g.save();
    g.translate(e.x, e.y);
    g.rotate(e.a);
    // poptart body (rounded rectangle) with pink frosting
    const bw = r * 1.7, bh = r * 1.25;
    g.fillStyle = "#e8b06a"; // pastry edge
    roundRect(g, -bw / 2, -bh / 2, bw, bh, r * 0.3); g.fill();
    g.fillStyle = "#ff8ad0"; // frosting
    roundRect(g, -bw / 2 + r * 0.18, -bh / 2 + r * 0.18, bw - r * 0.36, bh - r * 0.36, r * 0.22); g.fill();
    // sprinkles
    const spr = [["#33aaff", -0.3, -0.15], ["#ffe24a", 0.1, 0.2], ["#33dd55", 0.35, -0.1], ["#fff", -0.05, -0.25]];
    for (const [c, sx, sy] of spr) { g.fillStyle = c; g.fillRect(sx * bw, sy * bh, r * 0.22, r * 0.22); }
    // little grey cat head poking out the front
    g.fillStyle = "#9aa3ad";
    g.beginPath(); g.arc(bw * 0.45, 0, r * 0.6, 0, Math.PI * 2); g.fill();
    g.beginPath(); // ears
    g.moveTo(bw * 0.45 - r * 0.5, -r * 0.4); g.lineTo(bw * 0.45 - r * 0.2, -r * 0.85); g.lineTo(bw * 0.45 - r * 0.05, -r * 0.4);
    g.moveTo(bw * 0.45 + r * 0.5, -r * 0.4); g.lineTo(bw * 0.45 + r * 0.2, -r * 0.85); g.lineTo(bw * 0.45 + r * 0.05, -r * 0.4);
    g.fill();
    g.fillStyle = "#111318"; // eyes
    g.beginPath(); g.arc(bw * 0.45 + r * 0.18, -r * 0.05, r * 0.1, 0, 7); g.fill();
    g.restore();
}

// Doge pellet: a tiny shiba face. Small and fast, so kept to a
// readable minimum.
function doge(g, e) {
    const r = e.r * (e.sc || 1);
    g.save();
    g.translate(e.x, e.y);
    g.fillStyle = "#e7b45a";
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill();
    // ears
    g.fillStyle = "#d59f45";
    g.beginPath();
    g.moveTo(-r * 0.9, -r * 0.5); g.lineTo(-r * 0.5, -r * 1.15); g.lineTo(-r * 0.2, -r * 0.55);
    g.moveTo(r * 0.9, -r * 0.5); g.lineTo(r * 0.5, -r * 1.15); g.lineTo(r * 0.2, -r * 0.55);
    g.fill();
    // muzzle + squint eyes
    g.fillStyle = "#fff2d8";
    g.beginPath(); g.arc(0, r * 0.35, r * 0.45, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#111318";
    g.beginPath(); g.arc(-r * 0.35, -r * 0.05, r * 0.12, 0, 7); g.fill();
    g.beginPath(); g.arc(r * 0.35, -r * 0.05, r * 0.12, 0, 7); g.fill();
    g.beginPath(); g.arc(0, r * 0.25, r * 0.1, 0, 7); g.fill();
    g.restore();
}

function roundRect(g, x, y, w, h, rad) {
    g.beginPath();
    g.moveTo(x + rad, y);
    g.arcTo(x + w, y, x + w, y + h, rad);
    g.arcTo(x + w, y + h, x, y + h, rad);
    g.arcTo(x, y + h, x, y, rad);
    g.arcTo(x, y, x + w, y, rad);
    g.closePath();
}

// "This Is Fine" hazard as PIXEL-STYLE FIRE: chunky columns of square
// pixels whose heights animate smoothly over time into triangular
// peaks. The hot core (base + centre) is yellow; tips and edges cool
// through orange to red. performance.now() drives the motion — pure
// presentation, no game state reads it, so it can be
// non-deterministic. Tune the look via the FIRE_* consts.
const FIRE_PIX_DIV = 8;      // pixel size = radius / this (smaller = chunkier)
const FIRE_BASE = 0.3;       // min column height as a fraction of radius
const FIRE_PEAK = 1.15;      // extra height at the centre (× envelope)
const FIRE_SPEED = 2.4;      // primary flicker speed
const FIRE_COLORS = ["#ffe24a", "#ff8a1a", "#ff3b1a"]; // hot→cool (yellow→red)

const triWave = (x) => { const f = x - Math.floor(x); return 1 - Math.abs(2 * f - 1); };

function zone(g, e) {
    const R = e.sc || 40;                 // scale carries the zone radius
    const t = (typeof performance !== "undefined" ? performance.now() : 0) / 1000;
    const PIX = Math.max(3, R / FIRE_PIX_DIV);
    const baseY = e.y + R * 0.15;         // flames rise from just below the centre

    g.save();
    // warm ember glow pooled on the ground under the flames
    g.globalAlpha = 0.22;
    const glow = g.createRadialGradient(e.x, baseY, R * 0.12, e.x, baseY, R);
    glow.addColorStop(0, "#ffd24a");
    glow.addColorStop(1, "rgba(255,60,0,0)");
    g.fillStyle = glow;
    g.beginPath(); g.arc(e.x, baseY, R, 0, Math.PI * 2); g.fill();
    g.globalAlpha = 1;

    const cols = Math.max(1, Math.round((2 * R) / PIX));
    for (let i = 0; i <= cols; i++) {
        const colX = e.x - R + i * PIX;
        const dx = (colX + PIX / 2 - e.x) / R;         // -1..1 across the fire
        const env = 1 - dx * dx;                       // parabolic envelope, tall centre
        if (env <= 0) continue;
        // layered triangle waves → smoothly-animated triangular peaks
        // per column
        const flick = 0.5 + 0.5 * triWave(i * 0.5 + t * FIRE_SPEED) *
                                                (0.6 + 0.4 * triWave(i * 0.19 - t * 1.5));
        const h = R * (FIRE_BASE + FIRE_PEAK * env) * flick;
        const nPix = Math.max(1, Math.round(h / PIX));
        for (let p = 0; p < nPix; p++) {
            const up = nPix > 1 ? p / (nPix - 1) : 0;    // 0 = base, 1 = tip
            // heat: 1 hot (yellow) at base+centre, 0 cool (red) at
            // tip+edge
            const heat = (1 - up) * (1 - 0.45 * Math.abs(dx));
            g.fillStyle = heat > 0.62 ? FIRE_COLORS[0] : heat > 0.34 ? FIRE_COLORS[1] : FIRE_COLORS[2];
            g.fillRect(Math.round(colX), Math.round(baseY - (p + 1) * PIX), Math.ceil(PIX), Math.ceil(PIX) + 1);
        }
    }
    g.restore();
}

// A cereal O, tumbling. Small and plain — nine of these are on screen
// at once and they're 10px across.
function cereal(g, e) {
    const r = e.r * (e.sc || 1);
    g.save();
    g.translate(e.x, e.y);
    g.rotate(e.a);
    g.fillStyle = "#f3e6c4";                       // a fleck of milk trailing it
    g.globalAlpha = 0.5;
    g.beginPath(); g.arc(-r * 1.1, 0, r * 0.4, 0, 7); g.fill();
    g.globalAlpha = 1;
    g.fillStyle = e.c;
    g.beginPath(); g.arc(0, 0, r, 0, 7); g.fill();
    g.fillStyle = "#0f1420";                       // the hole
    g.beginPath(); g.arc(0, 0, r * 0.36, 0, 7); g.fill();
    g.restore();
}

// Stonks: a green arrow, always angled up-and-right regardless of
// where it's headed, because that is the entire joke. Its actual
// heading is in the trail.
function stonks(g, e) {
    const r = e.r * (e.sc || 1);
    g.save();
    g.translate(e.x, e.y);
    g.rotate(e.a);
    g.strokeStyle = "rgba(77,255,166,.35)";        // the flight path it's climbing out of
    g.lineWidth = Math.max(1, r * 0.25);
    g.lineCap = "round";
    g.beginPath(); g.moveTo(-r * 2.4, r * 0.5); g.lineTo(-r * 0.4, 0); g.stroke();
    g.restore();

    g.save();
    g.translate(e.x, e.y);
    g.rotate(-Math.PI / 4);                        // up and to the right. only.
    g.strokeStyle = e.c;
    g.lineWidth = Math.max(1.6, r * 0.34);
    g.lineCap = "round";
    g.beginPath(); g.moveTo(-r * 0.9, 0); g.lineTo(r * 0.3, 0); g.stroke();
    g.fillStyle = e.c;
    g.beginPath();                                 // arrowhead
    g.moveTo(r * 1.05, 0);
    g.lineTo(r * 0.15, -r * 0.62);
    g.lineTo(r * 0.15, r * 0.62);
    g.closePath();
    g.fill();
    g.restore();
}

// Me Gusta: the face, drifting after you. Upright always — a rage
// face reads as a face or it reads as nothing, and it doesn't tumble.
function gusta(g, e) {
    const r = e.r * (e.sc || 1);
    g.save();
    g.translate(e.x, e.y);
    g.fillStyle = e.c;
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill();
    g.strokeStyle = "#1a1a1a";
    g.lineWidth = Math.max(1, r * 0.1);
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke();
    gustaFace(g, 0, 0, r);
    g.restore();
}

// An item waiting on the map. Bobs, sits in a glowing ring, and — for
// weapons — shows the very art you'd be holding, so what's on the
// floor and what's in your hands are recognisably the same object.
function pickup(g, e) {
    const def = ITEMS[e.pk];
    // A plugin item (a ration, a crop) isn't in the client's own table — it rides a render HINT on the
    // snapshot instead: an icon key (e.ik) + tint (e.co) the server stamped from the item spec.
    if (!def && !e.ik) return;
    const kind = def ? def.give : null;
    const r = 13;
    const t = performance.now() / 1000;
    const y = e.y + Math.sin(t * 2 + (e.bob || 0)) * 3;   // slow bob, out of phase per item
    const ink = e.co || (kind === "health" ? "#4dffa6" : kind === "armor" ? "#4aa8ff" : kind === "jetpack" ? "#ff9a3a" : "#ffe24a");

    g.save();
    g.translate(e.x, y);
    // the pad it floats over — reads as "something is here" from
    // across the screen, which the item itself is too small to do
    g.globalAlpha = 0.22;
    const glow = g.createRadialGradient(0, 0, 2, 0, 0, r * 1.9);
    glow.addColorStop(0, ink);
    glow.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = glow;
    g.beginPath(); g.arc(0, 0, r * 1.9, 0, Math.PI * 2); g.fill();
    g.globalAlpha = 1;
    g.strokeStyle = ink;
    g.lineWidth = 1.5;
    g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke();

    if (kind === "health") {
        g.fillStyle = ink;                       // a cross
        g.fillRect(-2.5, -8, 5, 16);
        g.fillRect(-8, -2.5, 16, 5);
    } else if (kind === "armor") {
        g.fillStyle = ink;                       // a shield
        g.beginPath();
        g.moveTo(0, -9); g.lineTo(8, -5); g.lineTo(8, 3);
        g.quadraticCurveTo(8, 8, 0, 10);
        g.quadraticCurveTo(-8, 8, -8, 3);
        g.lineTo(-8, -5);
        g.closePath();
        g.fill();
    } else if (kind === "jetpack") {
        g.fillStyle = ink;                       // twin tanks with a flame
        g.fillRect(-6, -8, 4.5, 14);
        g.fillRect(1.5, -8, 4.5, 14);
        g.beginPath();
        g.moveTo(-3.5, 6); g.lineTo(3.5, 6); g.lineTo(0, 11);
        g.closePath(); g.fill();
    } else if (def) {
        const w = WEAPONS[def.weapon];
        const art = w && WEAPON_ART[w.shape];
        if (art) {
            // Drawn at the figure's own scale so it looks like the
            // thing the player holds, then nudged left because the
            // art hangs off a hand at its origin, not centred.
            g.translate(-5, 0);
            g.scale(0.85, 0.85);
            art(g, 13, ink);
        }
    } else {
        drawItemIcon(g, e.pk, e.ik, ink); // a plugin item: its own icon() art, else the render-hint key
    }
    g.restore();
}

// The icon palette for plugin items (a server-only plugin can't ship client art, so it picks a key
// from here + a tint). Keep these simple, centred, and readable at ~13px. Unknown key → a plain pip.
export function pickupIcon(g, key, ink) {
    g.fillStyle = ink; g.strokeStyle = ink; g.lineWidth = 1.6; g.lineJoin = "round";
    if (key === "food") {                         // an apple with a little stem
        g.beginPath(); g.arc(0, 1.5, 6, 0, Math.PI * 2); g.fill();
        g.strokeStyle = "#5a8a3a";
        g.beginPath(); g.moveTo(0.5, -4); g.quadraticCurveTo(3, -8, 6, -8); g.stroke();
    } else if (key === "seed") {                  // a pair of seeds
        g.save(); g.rotate(-0.5); g.beginPath(); g.ellipse(-2.5, 0, 2.4, 4.4, 0, 0, Math.PI * 2); g.fill(); g.restore();
        g.save(); g.rotate(0.5); g.beginPath(); g.ellipse(2.5, 1, 2.4, 4.4, 0, 0, Math.PI * 2); g.fill(); g.restore();
    } else if (key === "sprout") {                // a young two-leaf sprout on a short stem
        g.beginPath(); g.moveTo(0, 8); g.lineTo(0, -1); g.stroke();
        g.save(); g.rotate(0.8); g.beginPath(); g.ellipse(-3, -1, 3, 1.6, 0, 0, Math.PI * 2); g.fill(); g.restore();
        g.save(); g.rotate(-0.8); g.beginPath(); g.ellipse(3, -1, 3, 1.6, 0, 0, Math.PI * 2); g.fill(); g.restore();
    } else if (key === "crop") {                  // a leafy sprig
        g.beginPath(); g.moveTo(0, 8); g.lineTo(0, -7); g.stroke();
        g.save(); g.rotate(0.7); g.beginPath(); g.ellipse(-3.5, -2, 3.4, 1.7, 0, 0, Math.PI * 2); g.fill(); g.restore();
        g.save(); g.rotate(-0.7); g.beginPath(); g.ellipse(3.5, -5, 3.4, 1.7, 0, 0, Math.PI * 2); g.fill(); g.restore();
    } else if (key === "fire") {                  // a campfire flame
        g.beginPath();
        g.moveTo(0, 8);
        g.quadraticCurveTo(-7, 2, -3, -4);
        g.quadraticCurveTo(-3, -8, 1, -9);
        g.quadraticCurveTo(-0.5, -4, 3, -6);
        g.quadraticCurveTo(7, 0, 3, 7);
        g.closePath(); g.fill();
    } else if (key === "meal") {                  // a drumstick — a cooked meal
        g.beginPath(); g.arc(1.5, -2, 5.5, 0, Math.PI * 2); g.fill();     // the meat
        g.strokeStyle = "#efe0bb"; g.lineWidth = 2.6;
        g.beginPath(); g.moveTo(4, 1); g.lineTo(8.5, 6.5); g.stroke();    // the bone
    } else {                                      // generic pip
        g.beginPath(); g.arc(0, 0, 5, 0, Math.PI * 2); g.fill();
    }
}

// The ONE place "what a held item looks like" is decided (Set 14) — the hand's counterpart to
// drawItemIcon below. Called in the WEAPON FRAME: hand at the origin, +x down the aim, already
// mirrored upright when aiming left, so the art never thinks about where the player is or which way
// they face. `r` is the wielder's body radius, so everything scales with the figure.
//
// The order is the point. A plugin may give an item its own IN-HAND art (client.lua's hold_art);
// a weapon draws its silhouette; and ANYTHING ELSE falls back to the item's ICON, shrunk into the
// hand. That last line is what makes holding a seed look like holding a seed without a plugin doing
// any work at all — "holding the item defaults to the item graphic".
export function drawHeldItem(g, item, r, ink) {
    if (!item) return;
    const vm = _spriteVM;
    if (vm && vm.hasHold && vm.hasHold(item)) { vm.drawHold(item, g, r, ink); return; }

    const w = WEAPONS[item];
    const art = w && WEAPON_ART[w.shape];
    if (art) { art(g, r, ink); return; }

    // The default: the item's own icon, in hand. drawItemIcon draws around the origin at ~13px, so
    // scale it to the figure and undo the aim rotation — an icon is a picture of a thing, not a
    // barrel, so it should stay upright rather than point where you aim.
    const def = _itemDefs && _itemDefs(item);
    g.save();
    g.rotate(-(g.__aim || 0));
    g.scale((r / 13) * 0.9, (r / 13) * 0.9);
    drawItemIcon(g, item, def ? def.icon : "", (def && def.tint) || ink);
    g.restore();
}

// How the client looks an item's manifest up (icon key + tint) — set by RenderService from the
// room's welcome, since the item table is the server's. Absent in isolated tests: the icon path
// still runs, it just has no render hint to pass.
let _itemDefs = null;
export function setItemDefs(fn) { _itemDefs = fn; _raster.clear(); }

// Custom-block designs (Set 19.2): id -> a 36-int 6×6 pixel grid, so a `block-c<id>` item draws its
// ACTUAL design in the inventory/wheel, not a generic glyph. Set by RenderService from the client's
// synced terrain registry; cleared raster so a design arriving mid-game re-rasterises its icon.
let _blockDefs = null;
export function setBlockDefs(fn) { _blockDefs = fn; _raster.clear(); }
export const customBlockId = (item) => { const m = /^block-c(\d+)$/.exec(item || ""); return m ? +m[1] : null; };
// Draw a custom block's 6×6 pixel design centred on the origin (icon space ≈ ±7px), painted pixels in
// their palette colour, uncoloured ones in the neutral `custom` fill. Shared by the item icon + the wheel.
export function drawCustomBlockIcon(g, def, size = 14) {
    const s = size / 6, o = -size / 2;
    for (let row = 0; row < 6; row++) for (let col = 0; col < 6; col++) {
        const v = def ? def[row * 6 + col] : -1;
        if (v < 0) continue; // hollow subtile (Set 20.2) — a hole, drawn as nothing
        g.fillStyle = paintColor(v);
        g.fillRect(o + col * s, o + row * s, s + 0.4, s + 0.4);
    }
}

// --- the icon RASTER cache -----------------------------------------------------------------------
// Item icons are vector art, and a lot of it is plugin Lua (items/client.lua's palette). The inventory
// panel already rendered each one to a data URL once, but the ground pickups, the HUD strip, the wheel
// and the held-item fallback all re-ran the drawing EVERY FRAME — per pickup, per frame. Materials
// becoming items (Set 14) made that worse: there are more icons on screen now.
//
// So draw each (item, key, tint) ONCE into an offscreen canvas and blit it thereafter. Supersampled,
// because callers scale the result (the HUD strip by 1.4, the wheel by 1.6) and a 1:1 raster would go
// soft. The canvas FACTORY is injected — there's no document in the headless sims, and without one this
// falls straight through to the vector path, which is also what keeps those tests meaningful.
const ICON_BOX = 32;   // logical px the art is drawn within (it works around the origin at ~13px)
const ICON_SS = 4;     // supersample: crisp when a caller scales up
let _iconCanvas = null;
const _raster = new Map();

// Injected by RenderService in the browser. Setting it (or the VM / item defs below) drops the cache:
// a different room's plugins draw different art for the same item name, and a stale blit would show
// the previous room's icons.
export function setIconCanvas(fn) { _iconCanvas = fn; _raster.clear(); }
export function clearIconCache() { _raster.clear(); }

function iconRaster(item, key, ink) {
    if (!_iconCanvas) return null;
    const k = `${item}|${key}|${ink}`;
    if (_raster.has(k)) return _raster.get(k);
    let out = null;
    try {
        const c = _iconCanvas(ICON_BOX * ICON_SS, ICON_BOX * ICON_SS);
        const g2 = c && c.getContext && c.getContext("2d");
        if (g2) {
            g2.scale(ICON_SS, ICON_SS);
            g2.translate(ICON_BOX / 2, ICON_BOX / 2);
            drawItemIconVector(g2, item, key, ink);
            out = c;
        }
    } catch { out = null; }   // a broken icon caches its failure rather than throwing every frame
    _raster.set(k, out);
    return out;
}

// What every caller uses: the cached raster if we can make one, the vector art otherwise. Draws around
// the origin at the same size either way, so a caller's own transform is unchanged.
export function drawItemIcon(g, item, key, ink) {
    const c = iconRaster(item, key, ink);
    if (!c) { drawItemIconVector(g, item, key, ink); return; }
    g.drawImage(c, -ICON_BOX / 2, -ICON_BOX / 2, ICON_BOX, ICON_BOX);
}

// The ONE place "what an item looks like" is decided (Set 12.5). A plugin can OWN its item's icon by
// drawing it in client.lua (icon("name", fn)); if it did, draw that. Otherwise fall back to the built-in
// pickupIcon shape for the item's manifest key. Draws around the origin; used by the inventory + ground
// pickups alike, so an item looks the same everywhere.
function drawItemIconVector(g, item, key, ink) {
    const vm = _spriteVM, tint = ink || "#cdd8f5";
    // A custom block (Set 19.2) draws its own 6×6 pixel design, looked up by id from the synced registry.
    const cid = customBlockId(item);
    if (cid != null && _blockDefs) { const def = _blockDefs(cid); if (def) { drawCustomBlockIcon(g, def, 14); return; } }
    if (vm && vm.hasIcon) {
        if (item && vm.hasIcon(item)) { vm.drawIcon(item, g, tint); return; }  // a plugin's per-item override
        if (key && vm.hasIcon(key)) { vm.drawIcon(key, g, tint); return; }      // the items-plugin icon palette
    }
    // Weapons are items now (Set 12.9): an item that IS a weapon draws the very silhouette you hold
    // (weapons-art), so a held gun looks the same on the floor, in the hand, and in the inventory. This
    // is the engine's own art — the same "kept for now" fallback pickupIcon is, until weapon art moves
    // to a plugin too. drawWeaponIcon self-colours the shape, so the tint is only a hint here.
    const w = WEAPONS[item];
    if (w && WEAPON_ART[w.shape]) { drawWeaponIcon(g, w.shape, 0, 0, 22, tint); return; }
    pickupIcon(g, key || "", tint);                                            // engine fallback (kept for now)
}

// The Fish Launcher's fish: a little teardrop body with a forked tail, a dorsal fin, a
// gormless eye and a downturned mouth. Oriented along its travel angle, but mirrored
// upright whenever it's pointing left so the dorsal fin never ends up underneath — a fish
// arcing across the map (or flopping on the ground) always looks right-side up.
function fish(g, e) {
    const r = e.r * (e.sc || 1);
    g.save();
    g.translate(e.x, e.y);
    g.rotate(e.a || 0);
    if (Math.cos(e.a || 0) < 0) g.scale(1, -1); // keep it belly-down when facing left
    const dark = "#0b1a2b";

    g.fillStyle = e.c;
    // tail fin, trailing behind (−x)
    g.beginPath();
    g.moveTo(-r * 1.2, 0);
    g.lineTo(-r * 2.1, -r * 1.0);
    g.lineTo(-r * 1.6, 0);
    g.lineTo(-r * 2.1, r * 1.0);
    g.closePath();
    g.fill();
    // dorsal fin, on top
    g.beginPath();
    g.moveTo(-r * 0.3, -r * 0.85);
    g.lineTo(r * 0.35, -r * 1.6);
    g.lineTo(r * 0.7, -r * 0.7);
    g.closePath();
    g.fill();
    // body
    g.beginPath();
    g.ellipse(0, 0, r * 1.75, r * 0.95, 0, 0, Math.PI * 2);
    g.fill();
    // pale belly
    g.globalAlpha = 0.4; g.fillStyle = "#ffffff";
    g.beginPath(); g.ellipse(r * 0.1, r * 0.28, r * 1.25, r * 0.45, 0, 0, Math.PI * 2); g.fill();
    g.globalAlpha = 1;
    // gill line
    g.strokeStyle = dark; g.lineWidth = Math.max(1, r * 0.1);
    g.beginPath(); g.arc(r * 0.55, 0, r * 0.7, -1.1, 1.1); g.stroke();
    // eye
    g.fillStyle = dark;
    g.beginPath(); g.arc(r * 1.05, -r * 0.28, r * 0.26, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#fff";
    g.beginPath(); g.arc(r * 1.12, -r * 0.35, r * 0.09, 0, Math.PI * 2); g.fill();
    // downturned mouth
    g.strokeStyle = dark; g.lineWidth = Math.max(1, r * 0.12);
    g.beginPath(); g.arc(r * 1.55, -r * 0.15, r * 0.32, 0.5, 1.9); g.stroke();
    g.restore();
}

// (The CTF flag moved to the ctf plugin's client.lua in Set 17, with the rest of the mode.)

// The shape table. Declared here, below every routine it names, so it
// reads as a list of what exists rather than a promise that they turn
// up later.
const DRAW = { stick, cat, orb, blast, beam, poptart, doge, zone, cereal, stonks, gusta, pickup, fish };

// The jetpack worn on a player's back, with a live flame while thrusting. Purely
// cosmetic — the sim (the jetpack plugin) is the source of truth for jp/jf.
function drawJetpack(g, e) {
    const r = e.r * (e.sc || 1);
    const back = e.fl ? 1 : -1;           // the pack rides on the back, opposite the facing
    const bx = e.x + back * r * 0.55, by = e.y - r * 0.15;
    const w = r * 0.5, h = r * 1.05;
    g.save();
    g.fillStyle = "#48506a";
    g.strokeStyle = "#aeb8d6";
    g.lineWidth = Math.max(1, r * 0.09);
    g.beginPath();
    g.rect(bx - w / 2, by - h / 2, w, h);
    g.fill();
    g.stroke();
    // nozzle
    g.fillStyle = "#2c3244";
    g.fillRect(bx - w * 0.3, by + h / 2, w * 0.6, r * 0.18);
    if (e.jf) {
        // A flickering flame out of the nozzle — flame length jitters frame to frame.
        const fx = bx, fy = by + h / 2 + r * 0.18;
        const len = r * (0.9 + Math.random() * 0.7);
        const wide = r * 0.34;
        g.fillStyle = "#ffcf3a";
        g.beginPath();
        g.moveTo(fx - wide, fy); g.lineTo(fx + wide, fy); g.lineTo(fx, fy + len);
        g.closePath(); g.fill();
        g.fillStyle = "#ff7a1a";
        g.beginPath();
        g.moveTo(fx - wide * 0.55, fy); g.lineTo(fx + wide * 0.55, fy); g.lineTo(fx, fy + len * 0.7);
        g.closePath(); g.fill();
    }
    g.restore();
}

// The plugin sprite VM (Set 12.5): entities whose sprite `sh` no JS handler owns fall through to a
// plugin's client.lua draw fn. Set by RenderService from the room's welcome; time is the wall clock.
let _spriteVM = null, _spriteTime = 0;
export function setSpriteVM(vm) { _spriteVM = vm; _raster.clear(); } // new plugins -> new art
export function getSpriteVM() { return _spriteVM; } // client-plugin runtime: input dispatch reaches it
export function setSpriteTime(t) { _spriteTime = t; }

export function drawSprite(g, e) {
    const fn = DRAW[e.sh];
    if (fn) fn(g, e);
    else if (_spriteVM && _spriteVM.has(e.sh)) _spriteVM.draw(e.sh, g, e, _spriteTime); // a plugin's custom sprite
    else orb(g, e);
    if (e.jp) drawJetpack(g, e);
    // health bar for substantial bodies
    if (e.mx >= 10) {
        const r = e.r * (e.sc || 1);
        const w = Math.max(22, r * 2.2);
        const frac = Math.max(0, Math.min(1, e.hp / e.mx));
        const y = e.y - r - 12;
        g.fillStyle = "rgba(0,0,0,.5)";
        g.fillRect(e.x - w / 2, y, w, 4);
        g.fillStyle = e.tm === "players" ? "#39d1ff" : "#ff5b6e";
        g.fillRect(e.x - w / 2, y, w * frac, 4);
    }
}
